import * as L from "leaflet";

/**
 * Style of the background map (OpenFreeMap vector tiles: free, commercial use included, no key).
 * Other styles: "positron" (light grey, minimal), "liberty" (colourful, softer).
 */
const OPEN_FREE_MAP_STYLE = "https://tiles.openfreemap.org/styles/bright";

/** Layers of the style not wanted on our maps (ferry routes: dashed lines over the sea) */
const HIDDEN_LAYERS = ["ferry"];

/** Attribution required by the OpenFreeMap / OpenMapTiles / OpenStreetMap licences */
const OPEN_FREE_MAP_ATTRIBUTION =
  '<a href="https://openfreemap.org" target="_blank" rel="noopener">OpenFreeMap</a> ' +
  '<a href="https://www.openmaptiles.org/" target="_blank" rel="noopener">© OpenMapTiles</a> ' +
  'Data from <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>';

/**
 * Add the background map to a Leaflet map: OpenFreeMap vector tiles drawn by MapLibre, or the
 * standard OpenStreetMap raster tiles when the device cannot draw them (no WebGL) or MapLibre
 * cannot be loaded. Markers and the rest of the Leaflet code are not affected.
 *
 * MapLibre (~800 KB) is loaded on demand (dynamic import, separate chunk): only the pages that
 * actually show a map download it, not the app start-up.
 *
 * Place names (countries, regions, cities...) are shown in the given language when the map data
 * has them (name:it, name:de...), otherwise in Latin script or in the local name.
 *
 * @param map the Leaflet map
 * @param language language of the place names (the app language), Italian by default
 * @returns a function that switches the place names to another language (e.g. when the app
 *          language changes); it can be called before the map is ready, the last language wins
 */
export function addBaseMapLayer(map: L.Map, language: string = "it"): (language: string) => void {
  let currentLanguage = language;
  let vectorMap: any = null;
  let isStyleLoaded = false;
  // The page may close the map while MapLibre is still downloading
  let isMapRemoved = false;
  map.once("unload", () => (isMapRemoved = true));

  import("@maplibre/maplibre-gl-leaflet")
    .then(({ maplibreGL }) => {
      if (isMapRemoved) {
        return;
      }
      const layer = maplibreGL({
        style: OPEN_FREE_MAP_STYLE,
        attribution: OPEN_FREE_MAP_ATTRIBUTION,
      }).addTo(map);

      vectorMap = layer.getMaplibreMap();
      vectorMap.on("load", () => {
        HIDDEN_LAYERS.forEach((id) => {
          if (vectorMap.getLayer(id)) {
            vectorMap.setLayoutProperty(id, "visibility", "none");
          }
        });
        isStyleLoaded = true;
        showNamesInLanguage(vectorMap, currentLanguage);
      });
    })
    .catch(() => {
      if (!isMapRemoved) {
        addRasterFallback(map);
      }
    });

  return (newLanguage: string) => {
    currentLanguage = newLanguage;
    if (isStyleLoaded && vectorMap) {
      showNamesInLanguage(vectorMap, newLanguage);
    }
  };
}

/**
 * Standard OpenStreetMap raster tiles (names drawn in the images, they cannot change language)
 *
 * @param map the Leaflet map
 */
function addRasterFallback(map: L.Map): void {
  L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
    attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
  }).addTo(map);
}

/**
 * The style prefers English names (name_en): use the requested language instead, then the Latin
 * spelling, then the local name. Labels that are not names (e.g. road numbers) are left alone.
 *
 * @param vectorMap the MapLibre map inside the Leaflet layer
 * @param language two-letter language code
 */
function showNamesInLanguage(vectorMap: any, language: string): void {
  const nameExpression = [
    "coalesce",
    ["get", `name:${language}`],
    ["get", "name:latin"],
    ["get", "name"],
  ];
  for (const styleLayer of vectorMap.getStyle()?.layers ?? []) {
    const textField = styleLayer.layout?.["text-field"];
    if (textField && JSON.stringify(textField).includes("name")) {
      vectorMap.setLayoutProperty(styleLayer.id, "text-field", nameExpression);
    }
  }
}
